import { Alert, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CaptiveDevice } from '../api/captive';
import { deviceQualifier } from './format';

/**
 * Confirmation / wait-for-renewal screen (Amendment 02 Part A). Confirms the
 * device is registered and explains the short-lease mechanics: no user action is
 * needed — access opens by itself at the next address renewal (~1 min).
 *
 * The `activated` variant is the end of the self-activation branch: the account was
 * just activated, and the core disconnects the captive session, so the device asks
 * for a new (public) address immediately instead of waiting out its lease.
 *
 * ⚠ AND A FOURTH, `no_action` — the SERVICEABLE subscriber whose registration call
 * bound nothing because no captive lease backs his address (`outcome:
 * "no_device_identity"`, the backend half of D408). Nothing failed and nothing
 * changed: his account works and his connection already holds an ordinary address, so
 * the honest answer is that there is nothing to do here. It is a variant rather than
 * another negative because it is a positive statement about his situation, not the
 * absence of one — see ConfirmVariant in EnrollmentFlow for why it is neither an
 * error nor a retry.
 *
 * ⚠ AND THERE IS NOW A THIRD SHAPE, WHICH IS NOT A VARIANT BUT A NEGATIVE (D408).
 * Since a blocked subscriber may reach BlockedScreen with NO binding written (see
 * BLOCKING_STATES in EnrollmentFlow), pressing «Обновить» there after paying can land
 * him here on a session in which the registration never succeeded. «Устройство
 * зарегистрировано» would then be a claim nobody verified, on the one screen whose
 * whole job is to promise that access is opening. `registrationConfirmed={false}`
 * drops the device claim, keeps the (true) promise that the access is being restored,
 * and adds the one instruction that actually recovers an unbound device: open the
 * page again and log in.
 */
export function ConfirmationScreen({
  device,
  variant = 'registered',
  registrationConfirmed = true,
}: {
  device?: CaptiveDevice;
  variant?: 'registered' | 'activated' | 'no_action';
  /** False when no binding was written or confirmed in this session (D408). */
  registrationConfirmed?: boolean;
}) {
  const { t } = useTranslation();
  const qualifier = device ? deviceQualifier(device) : '';
  /**
   * The precedence, and every step of it is a decision:
   *
   *   no_action  first, because it is a POSITIVE statement about a situation in which
   *              nothing was written and nothing needed to be — it must not be
   *              overwritten by the generic «nothing was bound» shape below it.
   *   activated  next, because it describes a VERIFIED change of the account (the
   *              core's own `activated` flag plus a serviceable state) and says
   *              nothing about a device. An `awaiting_activation` subscriber who
   *              activates from an address with no captive lease has genuinely
   *              activated, and «Услуга активирована» is the true headline for him
   *              even though nothing was ever bound.
   *   unconfirmed then, for the ordinary registered/blocked-lifted path where a claim
   *              about the device would be unverified.
   */
  const noAction = variant === 'no_action';
  const activated = !noAction && variant === 'activated';
  const unconfirmed = !noAction && !activated && !registrationConfirmed;
  const titleKey = noAction
    ? 'confirmation.noActionTitle'
    : unconfirmed
      ? 'confirmation.unconfirmedTitle'
      : activated
        ? 'confirmation.activatedTitle'
        : 'confirmation.title';
  const subTitleKey = noAction
    ? 'confirmation.noActionSuccess'
    : unconfirmed
      ? 'confirmation.unconfirmedSuccess'
      : activated
        ? 'confirmation.activatedSuccess'
        : 'confirmation.success';
  const noteTitleKey = noAction ? 'confirmation.noActionNoteTitle' : 'confirmation.waitTitle';
  const waitKey = noAction
    ? 'confirmation.noActionWait'
    : unconfirmed
      ? 'confirmation.unconfirmedWait'
      : activated
        ? 'confirmation.activatedWait'
        : 'confirmation.wait';
  return (
    <Result
      status="success"
      title={t(titleKey)}
      subTitle={t(subTitleKey)}
      style={{ padding: '8px 0' }}
    >
      {device && (
        <div className="identity-panel" style={{ marginBottom: 16, textAlign: 'left' }}>
          <span className="group-label">{t('confirmation.deviceLabel')}</span>
          <div className="identity-row">
            <span className="k">{t('connection.mac')}</span>
            <span className="v mono">{device.mac}</span>
          </div>
          {qualifier && (
            <div className="identity-row">
              <span className="k">{t('connection.trap')}</span>
              <span className="v mono">{qualifier}</span>
            </div>
          )}
        </div>
      )}
      <Alert
        type="info"
        showIcon
        message={t(noteTitleKey)}
        description={t(waitKey)}
      />
    </Result>
  );
}
